// KI-Kontingent eines Mandanten — nur im Inhaberbereich.
//
// Jeder Belegscan und jede Frage kostet beim KI-Anbieter Geld. Der Tarif setzt
// die Regel, hier lässt sich für einen einzelnen Kunden davon abweichen: mehr
// für den, der viel scannt und dafür zahlt; weniger oder ganz aus für einen
// Ausreißer, ohne ihm gleich den Tarif zu entziehen.
//
// Leere Felder heißen „wie im Tarif" — so bleibt der Normalfall sichtbar
// normal, statt überall dieselbe Zahl doppelt zu pflegen.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import type { Mandant } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';

type Zeile = {
  tenantId: string;
  scans: number;
  fragen: number;
  tokenEin: number;
  tokenAus: number;
  grenzeScans: number;
  grenzeFragen: number;
  eigenesKontingent: { ki: boolean | null; scans: number | null; fragen: number | null } | null;
};

export default function KiKontingent({ mandant }: { mandant: Mandant }) {
  const [zeile, setZeile] = useState<Zeile | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [scans, setScans] = useState('');
  const [fragen, setFragen] = useState('');
  const [aus, setAus] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await api<{ mandanten: Zeile[] }>('/ki/owner/verbrauch');
      const meine = antwort.mandanten.find(m => m.tenantId === mandant.id) ?? null;
      setZeile(meine);
      setScans(meine?.eigenesKontingent?.scans != null ? String(meine.eigenesKontingent.scans) : '');
      setFragen(meine?.eigenesKontingent?.fragen != null ? String(meine.eigenesKontingent.fragen) : '');
      setAus(meine?.eigenesKontingent?.ki === false);
    } catch { /* der Block bleibt dann leer */ }
    finally { setLaedt(false); }
  }, [mandant.id]);

  useEffect(() => { void laden(); }, [laden]);

  async function speichern() {
    setSendet(true);
    setFehler(null);
    setMeldung(null);
    try {
      await api(`/ki/owner/kontingent/${mandant.id}`, {
        methode: 'PUT',
        daten: {
          ki: aus ? false : null,
          scans: scans.trim() === '' ? null : Number(scans),
          fragen: fragen.trim() === '' ? null : Number(fragen),
        },
      });
      setMeldung('Gespeichert. Der Mandant sieht die Änderung sofort.');
      await laden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Konnte nicht gespeichert werden.');
    } finally { setSendet(false); }
  }

  const eigen = !!zeile?.eigenesKontingent;
  // Grobe Hausnummer für den Monat. Der genaue Preis hängt vom Modell ab —
  // die Zahl soll nur zeigen, ob ein Kunde aus dem Rahmen fällt.
  const tokenGesamt = (zeile?.tokenEin ?? 0) + (zeile?.tokenAus ?? 0);

  return (
    <div>
      <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        KI-Assistent
        {eigen && <Abzeichen art="gelb">Sonderregel</Abzeichen>}
      </p>

      <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
        {laedt ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Wird geladen …</p>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Diesen Monat:{' '}
              <strong className="text-slate-900 dark:text-slate-100">{zeile?.scans ?? 0}</strong> von{' '}
              {grenzeText(zeile?.grenzeScans)} Belegscans ·{' '}
              <strong className="text-slate-900 dark:text-slate-100">{zeile?.fragen ?? 0}</strong> von{' '}
              {grenzeText(zeile?.grenzeFragen)} Fragen
              {tokenGesamt > 0 && (
                <span className="text-slate-500 dark:text-slate-400">
                  {' '}· {(tokenGesamt / 1000).toFixed(1)} Tsd. Token
                </span>
              )}
            </p>

            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <label className="block">
                <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Belegscans / Monat</span>
                <input value={scans} onChange={e => setScans(e.target.value)} inputMode="numeric"
                       placeholder="wie im Tarif"
                       className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Fragen / Monat</span>
                <input value={fragen} onChange={e => setFragen(e.target.value)} inputMode="numeric"
                       placeholder="wie im Tarif"
                       className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100" />
              </label>
              <Knopf variante="primaer" className="!text-xs" laedt={sendet} onClick={speichern}>
                Speichern
              </Knopf>
            </div>

            <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input type="checkbox" checked={aus} onChange={e => setAus(e.target.checked)}
                     className="h-4 w-4 rounded border-slate-300 text-marke-600" />
              KI für diesen Mandanten ganz abschalten
            </label>

            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              Leer lassen heißt: die Werte aus dem Tarif gelten. −1 bedeutet unbegrenzt.
            </p>

            {meldung && <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">{meldung}</p>}
            {fehler && <div className="mt-2"><Hinweis art="fehler">{fehler}</Hinweis></div>}
          </>
        )}
      </div>
    </div>
  );
}

function grenzeText(wert: number | undefined) {
  if (wert === undefined) return '—';
  return wert < 0 ? 'unbegrenzt' : String(wert);
}
